import moment from 'moment'
import html2canvas from 'html2canvas'
import { jsPDF } from 'jspdf'

import { useReactToPrint } from 'react-to-print'

import { jsx } from 'slate-hyperscript'
import { useTranslation } from 'react-i18next'
import {
  Clause,
  COMPOUND_TOKENS,
  NObject,
  PICKER_GROUPS,
  PickerGroup,
  TOKEN_TO_LABELS,
  TOKEN_TO_LOCATIONS
} from '../components/filter-variants'
import { specialInheritance } from '@/views/analyze/children/report-children/CurrentRepoet'
import { Orbit } from 'mdi-material-ui'

// import { execSync } from 'child_process'

const TIME_FORMAT = {
  timeFormat: `YYYY-MM-DD HH:mm:ss`,
  timeFormat2: `YYYY-MM-DD HH:mm`,
  timeFormat3: `YYYYMMDDHHmmss`
}

/**
 * 从字符串中提取指定指标的值
 * @param {string} text - 输入字符串
 * @param {string} key - 指标名称 (如 'REVEL', 'CADD')
 * @returns {number|null} - 提取到的数值，若未找到则返回 null
 */
export const extractMetricValue = (text: string, key: string) => {
  if (!text || !key) return null

  // 第一步：清洗字符串
  // 1. 移除 ASCII 控制字符 (\x00-\x1F, \x7F-\x9F)
  // 2. 移除常见的 Unicode 不可见字符 (零宽空格 \u200B, 零宽非连接符 \u200C 等)
  // 3. 移除 BOM 头 \uFEFF
  const cleanText = text.replace(
    // eslint-disable-next-line no-control-regex
    /[\x00-\x1F\x7F-\x9F\u200B-\u200D\uFEFF\u00A0]/g,
    ''
  )

  // 第二步：构建容错正则
  // 解释：
  // key        : 匹配指标名 (如 REVEL)
  // [\s\u00A0]*: 允许指标名和括号之间有任意空白或不间断空格
  // [(\uff08]  : 匹配半角 '(' 或 全角 '（'
  // \s*        : 允许括号内有空格
  // ([\d.]+)   : 捕获数字和小数点
  // \s*        : 允许数字后有空格
  // [)\uff09]  : 匹配半角 ')' 或 全角 '）'

  // 转义 key 中的特殊字符，防止正则注入
  const escapedKey = key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

  // 动态构建正则表达式
  const regex = new RegExp(
    `${escapedKey}[\\s\\u00A0]*[(\\uff08]\\s*([\\d.]+)\\s*[)\\uff09]`
  )

  const match = cleanText.match(regex)

  if (match && match) {
    return parseFloat(match[1])
  }

  return null
}

/**
 * 从包含JSON的字符串中提取并解析数组数据
 * @param {string} rawString - 可能包含引用标记和JSON数组的原始字符串
 * @returns {any[]|undefined} - 解析后的数组数据，如果解析失败返回空数组，未找到JSON返回undefined
 *
 * @description 此函数处理可能含有引用标记(<ref>[数字]</ref>)的字符串，
 * 移除这些标记后提取JSON数组部分并进行解析，具有错误处理机制确保安全运行。
 */
export function getArr(rawString: string) {
  try {
    // 1. 移除所有 <ref>[数字]</ref> 标记
    const cleanedString = rawString.replace(/<ref>\[\d+\]<\/ref>/g, '')

    // 2. 提取 JSON 部分（假设 JSON 以 `[` 开头，以 `]` 结尾）
    const jsonMatch = cleanedString.match(/\[[\s\S]*\]/)

    if (!jsonMatch) {
      console.error('未找到有效的 JSON 数据')

      return
    }
    const jsonString = jsonMatch[0]
    const parsedData = isJson(jsonString) ? JSON.parse(jsonString) : []

    return parsedData
  } catch (error) {
    //
    return []
  }
}

/**
 * 合并两个数组并根据特定规则进行去重处理
 * @param {any[]} array1 - 第一个待合并的数组
 * @param {any[]} array2 - 第二个待合并的数组
 * @returns {any[]} - 去重后的数组
 *
 * @description 此函数将两个数组合并，然后根据对象的title属性进行去重。
 * 在title相同时，优先保留包含hpo_code属性的记录，确保数据的完整性和优先级。
 */
export function deduplicateArrays(array1: any[], array2: any[]) {
  // 合并两组数据

  const mergedArray = [...array1, ...array2]

  // 使用reduce进行去重，优先保留有hpo的记录

  const deduped = mergedArray.reduce((acc, current) => {
    const existing = acc[current.title]

    // 条件1：当前记录有hpo且已存在记录无hpo

    // 条件2：当前记录是首次出现

    if (!existing || (current.hpo_code && !existing.hpo_code)) {
      acc[current.title] = current
    }

    return acc
  }, {})

  // 转换为数组返回

  return Object.values(deduped)
}

export function groupObjectBySize(
  originalObj: { [x: string]: any } | null,
  groupSize = 30
) {
  // 验证输入有效性

  if (typeof originalObj !== 'object' || originalObj === null) {
    throw new Error('输入必须是一个对象')
  }

  if (typeof groupSize !== 'number' || groupSize < 1) {
    throw new Error('分组大小必须是大于0的数字')
  }

  // 获取对象键并排序（保持顺序）

  const keys = Object.keys(originalObj).sort((a, b) => Number(a) - Number(b))

  const groups = []

  // 分组逻辑

  for (let i = 0; i < keys.length; i += groupSize) {
    const groupKeys = keys.slice(i, i + groupSize)

    const group: NoObject = {}

    groupKeys.forEach((key) => {
      group[key] = originalObj[key]
    })

    groups.push(group)
  }

  return groups
}

/**
 * 日期格式化
 * @param date 日期 |字符串 |时间戳
 * @param format 时间格式  默认：YYYY-MM-DD HH:mm:ss
 * @returns {string|null}
 */
export function formatDate(
  date: moment.MomentInput,
  format = TIME_FORMAT.timeFormat
) {
  if (!date) return null

  return moment(date).format(format)
}

export function filterObjectsByTextWithRemainder(
  array: any[],
  searchString: string
) {
  const sliceIndex = array.findIndex((item) =>
    item.some((i: any) => i.text && i.text.includes(searchString))
  )

  //

  return sliceIndex < 0 ? array : array.slice(0, sliceIndex)
}

/**
 * 日期格式化
 * @param chromosomeNum chrom
 * @param startPos
 * @param endPos
 * @param chromosomeData
 * @returns {string|null}
 */
export function chromosomeRegion(
  chromosomeNum: any,
  startPos: number,
  endPos: number,
  chromosomeData: any[]
) {
  if (!chromosomeData) {
    return 'Unknown chromosome'
  }

  let startRegion = ''
  let endRegion = ''

  chromosomeData.map((item) => {
    const { chromStart, chromEnd, name } = item

    if (startPos >= chromStart && startPos <= chromEnd) {
      startRegion = name
    }

    if (endPos >= chromStart && endPos <= chromEnd) {
      endRegion = name
    }
  })

  if (startRegion === endRegion) {
    return startRegion
  } else {
    return `${startRegion}-${endRegion}`
  }
}

/**
 * 根据年龄计算出生日期
 * @param age 年龄
 * @returns {Date|null}
 */
export function calculateBirthDate(age: number) {
  // 获取当前日期

  const now = new Date()

  // 计算出生年份

  const birthYear = now.getFullYear() - age

  // 设置出生日期的月份和日期为当前月份和日期

  // 注意：这可能导致问题，因为不是每个月都有相同的天数

  // 或者闰年的2月有29天，而非闰年只有28天

  const birthDate = new Date(birthYear, now.getMonth(), now.getDate())

  // 如果计算出的日期在当前年份之后（由于月份或日期的减少），则需要修正

  if (birthDate > now) {
    // 如果月份为0（即1月），则需要减少年份并设置月份为11（即12月）

    if (birthDate.getMonth() === 0) {
      birthDate.setFullYear(birthYear - 1)

      birthDate.setMonth(11) // 11代表12月
    } else {
      birthDate.setMonth(birthDate.getMonth() - 1)
    }

    // 尝试将日期设置为当前日期的日期（可能无效，如果那个月没有那么多天）

    birthDate.setDate(now.getDate())

    // 如果日期仍然无效（即大于那个月的最大天数），则设置为那个月的最后一天

    if (birthDate > now) {
      birthDate.setDate(0) // 设置为那个月的最后一天
    }
  }

  // 返回出生日期（可能是一个近似的日期，因为月份和日期的减少可能不总是精确的）

  return birthDate
}

/**
 * 判断字符串是否为url
 * @param str url
 * @returns {boolean}
 */
export function isValidURL(str: string) {
  try {
    const pattern = new RegExp(
      '^(https?:\\/\\/)?' + // protocol
        '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|' + // domain name
        '((\\d{1,3}\\.){3}\\d{1,3}))' + // OR ip (v4) address
        '(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*' + // port and path
        '(\\?[;&a-z\\d%_.~+=-]*)?' + // query string
        '(\\#[-a-z\\d_]*)?$',
      'i'
    ) // fragment locator

    return !!pattern.test(str)
  } catch (error) {
    return true
  }
}

/**
 * 对象数组去重
 * @param arr 去重数组
 * @param key 去重key
 * @returns {Array}
 */
export function unique(arr: any[], key: string | number) {
  const res = new Map()

  return arr.filter((arr) => !res.has(arr[key]) && res.set(arr[key], 1))
}

/**
 * 转化为数字字符串
 * @param input 字符串
 * @returns {string}
 */
export function addCommas(input: string) {
  // 检查输入是否为有效的数字字符串（可以包含小数点）

  const isNumeric = /^\d+(\.\d+)?$/.test(input)

  if (!isNumeric) {
    return input
  }

  const parts = input.split('.')

  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',')

  return parts.join('.')
}

/**
 * 判断是否为电脑端网页
 * @returns {boolean}
 */
export function isWindow() {
  let type = 'window'

  if (/(micromessenger)/i.test(navigator.userAgent)) {
    // 微信浏览器判断
    type = 'mobile'
  } else {
    // 判断是否为移动设备
    const isMobileDevice =
      /Android|webOS|iPhone|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent
      )

    // 判断是否为平板设备
    const isTablet =
      /iPad/i.test(navigator.userAgent) ||
      (/Android/i.test(navigator.userAgent) &&
        !/Mobile/i.test(navigator.userAgent)) ||
      (window.innerWidth >= 768 && window.innerWidth <= 1424)

    if (isMobileDevice) {
      // 是手机设备
      type = 'mobile'
    } else if (isTablet) {
      // 是平板设备
      type = 'tablet'
    } else {
      // 是PC或平板设备
      type = 'window'
    }
  }

  return type === 'window' || type === 'tablet' ? true : false
}

/**
 * 判断是否为json
 * @param key 字符串
 * @returns {Boolean}
 */
export function isJson(key: string) {
  try {
    JSON.parse(key)

    return true
  } catch (error) {
    return false
  }
}

/**
 * 函数防抖
 * @param func fn
 * @param wait 时间
 * @returns {fn}
 */
export function debounce(func: any, wait: number) {
  let timeout = null as any

  return function (...args: any) {
    const later = () => {
      clearTimeout(timeout)

      // @ts-ignore: Implicit 'any' type because no type annotation is present.
      func.apply(this as any, args)
    }

    clearTimeout(timeout)

    timeout = setTimeout(later, wait)
  }
}

/**
 * 导出为PDF
 * @param id 节点id
 * @returns {null}
 */
export function PrintHtmlToPDF(dom: HTMLElement, name = '') {
  // const dom = document.getElementById(id) as any //获取需要打印的区域

  html2canvas(dom).then((canvas) => {
    const contentWidth = canvas.width
    const contentHeight = canvas.height

    //一页pdf显示html页面生成的canvas高度;
    const pageHeight = (contentWidth / 592.28) * 841.89

    //未生成pdf的html页面高度
    let leftHeight = contentHeight

    //页面偏移
    let position = 0

    //a4纸的尺寸[595.28,841.89]，html页面生成的canvas在pdf中图片的宽高
    const imgWidth = 595.28
    const imgHeight = (592.28 / contentWidth) * contentHeight

    const pageData = canvas.toDataURL('image/jpeg', 1.0)

    const pdf = new jsPDF(undefined, 'pt', 'a4')

    //有两个高度需要区分，一个是html页面的实际高度，和生成pdf的页面高度(841.89)
    //当内容未超过pdf一页显示的范围，无需分页

    if (leftHeight < pageHeight) {
      pdf.addImage(pageData, 'JPEG', 0, 0, imgWidth, imgHeight)
    } else {
      while (leftHeight > 0) {
        pdf.addImage(pageData, 'JPEG', 0, position, imgWidth, imgHeight)

        leftHeight -= pageHeight

        position -= 841.89

        //避免添加空白页
        if (leftHeight > 0) {
          pdf.addPage()
        }
      }
    }

    pdf.save(`${name}.pdf`)
  })
}

/**
 * 导出为PDF
 * @param id 节点id
 * @returns {null}
 */
export function PrintReactHtmlToPDF(
  printRef: React.MutableRefObject<any>,
  name = ''
) {
  // 打印功能
  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    pageStyle: `
        @page {
          size: A4;
          margin: 0;
        }
        @media print {
          body, html {
            height: auto;
            margin: 0;
          }
          body{
            zoom: 100%;
          }
          img{
            max-width: 100% !important;
            height: auto !important;
            page-break-inside: auto;
            break-inside: auto;
          }
        }`,
    removeAfterPrint: true,
    documentTitle: `${name}.pdf`
  })

  handlePrint()
}

/**
 * 下载图片地址
 * @param imageUrl  图片地址imageUrl
 * @param name  下载图片文件名
 * @returns {null}
 */
export async function DownloadImage(imageUrl: string, name: string) {
  if (!imageUrl) {
    console.error('No image URL provided')

    return
  }

  try {
    const response = await fetch(imageUrl)

    if (!response.ok) {
      throw new Error('Network response was not ok')
    }

    const blob = await response.blob()

    // 创建一个指向blob对象的URL

    const url = window.URL.createObjectURL(blob)

    // 创建一个临时的a标签用于下载

    const link = document.createElement('a')

    link.href = url

    link.setAttribute('download', name + '.png') // 设置下载文件的名称

    document.body.appendChild(link)

    link.click()

    // 清理

    document.body.removeChild(link)

    window.URL.revokeObjectURL(url)
  } catch (error) {
    console.error('Failed to download image:', error)
  }
}

/**
 * 查找并返回第一个连续的数字序列
 * @param str  str
 * @returns {null | number}
 */
export async function extractFirstNumber(str: string) {
  // 使用正则表达式匹配第一个数字序列

  // \d+ 表示匹配一个或多个数字

  // ^ 表示匹配字符串的开始（但在这里我们不使用它，因为我们想在字符串中的任何位置查找）

  // \D* 表示匹配任意数量的非数字字符（前面的数字之前的）

  // \b 表示单词边界，确保我们匹配的是完整的单词（数字序列）

  const match = str.match(/\b\d+\b/)

  // 如果找到了匹配项，则返回匹配的数字字符串

  // 否则返回null或undefined（取决于你希望如何处理没有找到的情况）

  return match ? match[0] : null
}

interface NoObject {
  [key: string]: any
}

export function demo() {
  const NewElement = {
    ...Element,
    serializeWithStyles: null
  } as any

  NewElement.prototype = Element.prototype

  NewElement.prototype['serializeWithStyles'] = (function () {
    // Mapping between tag names and css default values lookup tables. This allows to exclude default values in the result.

    const defaultStylesByTagName: NoObject = {}

    // Styles inherited from style sheets will not be rendered for elements with these tag names

    const noStyleTags: NoObject = {
      BASE: true,
      HEAD: true,
      HTML: true,
      META: true,
      NOFRAME: true,
      NOSCRIPT: true,
      PARAM: true,
      SCRIPT: true,
      STYLE: true,
      TITLE: true
    }

    const tagNames = [
      'A',
      'ABBR',
      'ADDRESS',
      'AREA',
      'ARTICLE',
      'ASIDE',
      'AUDIO',
      'B',
      'BASE',
      'BDI',
      'BDO',
      'BLOCKQUOTE',
      'BODY',
      'BR',
      'BUTTON',
      'CANVAS',
      'CAPTION',
      'CENTER',
      'CITE',
      'CODE',
      'COL',
      'COLGROUP',
      'COMMAND',
      'DATALIST',
      'DD',
      'DEL',
      'DETAILS',
      'DFN',
      'DIV',
      'DL',
      'DT',
      'EM',
      'EMBED',
      'FIELDSET',
      'FIGCAPTION',
      'FIGURE',
      'FONT',
      'FOOTER',
      'FORM',
      'H1',
      'H2',
      'H3',
      'H4',
      'H5',
      'H6',
      'HEAD',
      'HEADER',
      'HGROUP',
      'HR',
      'HTML',
      'I',
      'IFRAME',
      'IMG',
      'INPUT',
      'INS',
      'KBD',
      'KEYGEN',
      'LABEL',
      'LEGEND',
      'LI',
      'LINK',
      'MAP',
      'MARK',
      'MATH',
      'MENU',
      'META',
      'METER',
      'NAV',
      'NOBR',
      'NOSCRIPT',
      'OBJECT',
      'OL',
      'OPTION',
      'OPTGROUP',
      'OUTPUT',
      'P',
      'PARAM',
      'PRE',
      'PROGRESS',
      'Q',
      'RP',
      'RT',
      'RUBY',
      'S',
      'SAMP',
      'SCRIPT',
      'SECTION',
      'SELECT',
      'SMALL',
      'SOURCE',
      'SPAN',
      'STRONG',
      'STYLE',
      'SUB',
      'SUMMARY',
      'SUP',
      'SVG',
      'TABLE',
      'TBODY',
      'TD',
      'TEXTAREA',
      'TFOOT',
      'TH',
      'THEAD',
      'TIME',
      'TITLE',
      'TR',
      'TRACK',
      'U',
      'UL',
      'VAR',
      'VIDEO',
      'WBR'
    ]

    // Precompute the lookup tables.

    for (let i = 0; i < tagNames.length; i++) {
      if (!noStyleTags[tagNames[i]]) {
        defaultStylesByTagName[tagNames[i]] = computeDefaultStyleByTagName(
          tagNames[i]
        )
      }
    }

    function computeDefaultStyleByTagName(tagName: string) {
      const defaultStyle: NoObject = {}

      const element = document.body.appendChild(document.createElement(tagName))

      const computedStyle: any = getComputedStyle(element)

      for (let i = 0; i < computedStyle.length; i++) {
        defaultStyle[computedStyle[i]] = computedStyle[computedStyle[i]]
      }

      document.body.removeChild(element)

      return defaultStyle
    }

    function getDefaultStyleByTagName(tagName: string) {
      tagName = tagName.toUpperCase()

      if (!defaultStylesByTagName[tagName]) {
        defaultStylesByTagName[tagName] = computeDefaultStyleByTagName(tagName)
      }

      return defaultStylesByTagName[tagName]
    }

    return function serializeWithStyles(this: any) {
      if (this.nodeType !== Node.ELEMENT_NODE) {
        throw new TypeError()
      }

      const cssTexts = []

      const elements = this.querySelectorAll('*')

      for (let i = 0; i < elements.length; i++) {
        const e = elements[i]

        if (!noStyleTags[e.tagName]) {
          const computedStyle: any = getComputedStyle(e)

          const defaultStyle = getDefaultStyleByTagName(e.tagName)

          cssTexts[i] = e.style.cssText

          for (let ii = 0; ii < computedStyle.length; ii++) {
            const cssPropName = computedStyle[ii]

            if (computedStyle[cssPropName] !== defaultStyle[cssPropName]) {
              e.style[cssPropName] = computedStyle[cssPropName]
            }
          }
        }
      }

      const result = this.outerHTML

      for (let i = 0; i < elements.length; i++) {
        elements[i].style.cssText = cssTexts[i]

        const e = elements[i]
      }

      return result
    }
  })()
}

const ELEMENT_TAGS: NoObject = {
  A: (el: any) => ({ type: 'link', url: el.getAttribute('href') }),
  BLOCKQUOTE: () => ({ type: 'quote' }),
  ARTICLE: () => ({ type: 'quote' }),
  H1: () => ({ type: 'heading-one' }),
  H2: () => ({ type: 'heading-two' }),
  H3: () => ({ type: 'heading-three' }),
  H4: () => ({ type: 'heading-four' }),
  H5: () => ({ type: 'heading-five' }),
  H6: () => ({ type: 'heading-six' }),
  IMG: (el: any) => ({ type: 'image', url: el.getAttribute('src') }),
  LI: () => ({ type: 'list-item' }),
  OL: () => ({ type: 'numbered-list' }),
  P: () => ({ type: 'paragraph' }),
  PRE: () => ({ type: 'code' }),
  TABLE: () => ({ type: 'table' }),
  TR: () => ({ type: 'table-row' }),
  TD: () => ({ type: 'table-cell' }),
  UL: () => ({ type: 'bulleted-list' })
}

// COMPAT: `B` is omitted here because Google Docs uses `<b>` in weird ways.
const TEXT_TAGS: NoObject = {
  CODE: () => ({ code: true }),
  DEL: () => ({ strikethrough: true }),
  EM: () => ({ italic: true }),
  I: () => ({ italic: true }),
  S: () => ({ strikethrough: true }),
  STRONG: () => ({ bold: true }),
  U: () => ({ underline: true })
}

export function deserialize(el: any) {
  if (el.nodeType === 3) {
    return el.textContent
  } else if (el.nodeType !== 1) {
    return null
  } else if (el.nodeName === 'BR') {
    return '\n'
  }

  const { nodeName } = el
  let parent = el

  if (
    nodeName === 'PRE' &&
    el.childNodes[0] &&
    el.childNodes[0].nodeName === 'CODE'
  ) {
    parent = el.childNodes[0]
  }
  let children: any = Array.from(parent.childNodes).map(deserialize).flat()

  if (children.length === 0) {
    children = [{ text: '' }]
  }

  if (el.nodeName === 'BODY') {
    return jsx('fragment', {}, children)
  }

  if (ELEMENT_TAGS[nodeName]) {
    const attrs = ELEMENT_TAGS[nodeName](el)

    return jsx('element', attrs, children)
  }

  if (TEXT_TAGS[nodeName]) {
    const attrs = TEXT_TAGS[nodeName](el)

    //

    return children.map((child: any) => jsx('text', attrs, child))
  }

  return children
}

export enum SpecialInheritance {
  AR = 'autosomal recessive',
  AD = 'autosomal dominant',
  XD = 'x-linked dominant',
  XR = 'x-linked recessive',
  X = 'x-linked'
}

export enum SpecialInheritanceShortNotation {
  AR = 'AR',
  AD = 'AD',
  XD = 'X-D',
  XR = 'X-R',
  X = 'X-linked'
}

export function switchVariantClass(variantClass: string) {
  if (variantClass === 'Gain' || variantClass === 'duplication') return 'DUP'
  else if (variantClass === 'Loss' || variantClass === 'deletion') return 'DEL'
  else return ''
}

export function calculateOverlapRate(
  cnv_start: number,
  cnv_end: number,
  sv_start: number,
  sv_end: number
) {
  // Validate inputs
  if (cnv_start > cnv_end || sv_start > sv_end) {
    return 0.0
  }

  // Calculate CNV length
  const cnv_length = cnv_end - cnv_start + 1

  if (cnv_length <= 0) {
    return 0.0
  }

  // Find overlap region
  const overlap_start = Math.max(cnv_start, sv_start)
  const overlap_end = Math.min(cnv_end, sv_end)

  // Check if there's any overlap
  if (overlap_start > overlap_end) {
    return 0.0
  }

  // Calculate overlap length
  const overlap_length = overlap_end - overlap_start + 1

  // Calculate overlap rate
  return overlap_length / cnv_length
}

export function calculateCoverage(
  interval1: [number, number],
  interval2: [number, number]
) {
  const [start1, end1] = interval1

  const [start2, end2] = interval2

  // 计算重叠部分

  const overlapStart = Math.max(start1, start2)

  const overlapEnd = Math.min(end1, end2)

  const overlapLength = Math.max(0, overlapEnd - overlapStart + 1)

  // 计算覆盖度

  const coverage = (overlapLength / (end2 - start2 + 1)) * 100

  return {
    interval1: { start: start1, end: end1, length: end1 - start1 + 1 },

    interval2: { start: start2, end: end2, length: end2 - start2 + 1 },

    overlap: { start: overlapStart, end: overlapEnd, length: overlapLength },

    coveragePercentage: coverage.toFixed(2) + '%'
  }
}

export function containsChinese(str: string) {
  for (const char of str) {
    const code = char.charCodeAt(0)

    // 判断是否在中文字符的 Unicode 范围内

    if (
      (code >= 0x4e00 && code <= 0x9fff) ||
      (code >= 0x3400 && code <= 0x4dbf) ||
      (code >= 0xf900 && code <= 0xfaff)
    ) {
      return true
    }
  }

  return false
}

export function containsEnglish(str: string) {
  // 匹配连续的英文字母序列

  return /[a-zA-Z]+/.test(str)
}

export function containsBoth(str: string) {
  return containsChinese(str) && containsEnglish(str)
} // 判断字符是否为中文（含扩展字符和全角符号）

export function formatVariantInheritance(inheritance: string) {
  const specialInheritances = Object.values(SpecialInheritance)
  const specialInheritanceShorts = Object.values(
    SpecialInheritanceShortNotation
  )

  const result: SpecialInheritanceShortNotation[] = []

  specialInheritances.forEach((e, i) => {
    if (typeof inheritance === 'string' && inheritance.includes(e)) {
      result.push(specialInheritanceShorts[i])
    }
  })

  return result.join(', ')
}

export function parseGenomicString(genomicString: string) {
  const parts = genomicString.split('_')

  const result: {
    chr: null | string
    pos: null | number
    ref: null | string
    alt: null | string
    assembly: null | string
  } = {
    chr: null,

    pos: null,

    ref: null,

    alt: null,

    assembly: null
  }

  // 基础格式校验（必须有5个部分）

  if (parts.length !== 5) return result

  const [chr, posStr, ref, alt, assembly] = parts

  const validChromosomes = new Set([
    '1',
    '2',
    '3',
    '4',
    '5',
    '6',
    '7',
    '8',
    '9',
    '10',
    '11',
    '12',
    '13',
    '14',
    '15',
    '16',
    '17',
    '18',
    '19',
    '20',
    '21',
    '22',
    'X',
    'Y',
    'MT'
  ])

  const validBases = new Set(['A', 'C', 'G', 'T'])

  // 染色体校验

  if (validChromosomes.has(chr)) {
    result.chr = chr
  }

  // 位置校验（必须是正整数）

  const pos = parseInt(posStr, 10)

  if (!isNaN(pos) && pos > 0) {
    result.pos = pos
  }

  // 参考碱基校验（必须是单个有效碱基）

  if (ref.length === 1 && validBases.has(ref.toUpperCase())) {
    result.ref = ref
  }

  // 变异碱基校验（必须是单个有效碱基）

  if (alt.length === 1 && validBases.has(alt.toUpperCase())) {
    result.alt = alt
  }

  // 基因组版本校验

  if (assembly === 'hg38' || assembly === 'hg19') {
    result.assembly = assembly
  } else {
    result.assembly = 'hg19'
  }

  return result
}

export function regexHP(phenotype: string) {
  // 使用正则表达式提取HP:后面的数字

  const regex = /HP:(\d+)/g

  let match

  const numbers = []

  while ((match = regex.exec(phenotype)) !== null) {
    numbers.push(match[1])
  }

  // 将数字转换为对象数组

  const result = numbers.map((number, index) => ({
    id: number
  }))

  return result
}

/**

 * 验证并转换三字母代码

 * @param {string} code - 氨基酸三字母代码

 * @returns {string} 氨基酸全称

 */
// 氨基酸三字母代码与全称映射表

const AMINO_ACID_MAP: NObject = {
  Ala: '丙氨酸',

  Arg: '精氨酸',

  Asn: '天冬酰胺',

  Asp: '天冬氨酸',

  Cys: '半胱氨酸',

  Gln: '谷氨酰胺',

  Glu: '谷氨酸',

  Gly: '甘氨酸',

  His: '组氨酸',

  Ile: '异亮氨酸',

  Leu: '亮氨酸',

  Lys: '赖氨酸',

  Met: '甲硫氨酸',

  Phe: '苯丙氨酸',

  Pro: '脯氨酸',

  Ser: '丝氨酸',

  Thr: '苏氨酸',

  Trp: '色氨酸',

  Tyr: '酪氨酸',

  Val: '缬氨酸',

  Ter: '终止密码子'
}

/**
 * 解析碱基变异格式，将HGVS标准格式转换为人类可读的中文描述
 *
 * @param inputStr - 输入的碱基变异字符串，支持 HGVS 标准格式
 * @returns 人类可读的中文变异描述，无法识别时返回"无法识别的碱基变异格式"
 *
 * @example
 * parseMutation("c.860_863dup")      // => "第860至863位碱基，发生重复突变"
 * parseMutation("c.123del")          // => "第123位碱基，发生缺失突变"
 * parseMutation("c.123delA")         // => "第123位碱基，缺失碱基"A""
 * parseMutation("c.123insA")         // => "第123位碱基，插入碱基序列"A""
 * parseMutation("c.123A>T")          // => "第123位碱基，由"A"变为"T""
 * parseMutation("c.123_124delinsAG") // => "第123至124位碱基，缺失并插入碱基序列"AG""
 *
 * @description
 * 支持的变异类型：
 * - DUP (重复): 碱基序列重复
 * - DEL (缺失): 碱基缺失，可带缺失碱基序列
 * - INS (插入): 碱基插入
 * - SUB (替换): 单个碱基替换
 * - DELINS (缺失插入): 先缺失再插入新序列
 */
export function parseMutation(inputStr: string): string {
  // 1. 预处理：去除空格，转为大写
  const cleanStr = inputStr.replace(/\s+/g, '').toUpperCase()

  // 辅助函数：格式化位置描述
  // 输入: "860_863" -> "第860至863位碱基"
  // 输入: "860"   -> "第860位碱基"
  const formatPosition = (posStr: string) => {
    if (!posStr) return '未知位置'
    if (posStr.includes('_')) {
      const [start, end] = posStr.split('_')

      // 确保 start 和 end 是纯数字，防止带入其他字符
      if (/^\d+$/.test(start) && /^\d+$/.test(end)) {
        return `第${start}至${end}位碱基`
      }
    }

    // 检查是否为纯数字
    if (/^\d+$/.test(posStr)) {
      return `第${posStr}位碱基`
    }
    return `位置${posStr}`
  }

  // 2. 定义更严格的正则表达式
  // 关键点：捕获组 () 内部只包含数字和下划线，不包含 c. 或 DUP 等前后缀

  // A. 重复 (dup): c.860_863dup -> 捕获 860_863
  const dupRegex = /(?:C\.)?(\d+(?:_\d+)?)DUP$/
  const dupMatch = cleanStr.match(dupRegex)
  if (dupMatch && dupMatch[1]) {
    return `${formatPosition(dupMatch[1])}，发生重复突变`
  }

  // B. 缺失 (del): c.123del 或 c.123_125del 或 c.123delA
  // 捕获组1: 位置, 捕获组2: 可选的缺失碱基
  const delRegex = /(?:C\.)?(\d+(?:_\d+)?)DEL([ATGC]*)?$/
  const delMatch = cleanStr.match(delRegex)
  if (delMatch && delMatch[1]) {
    const posStr = delMatch[1]
    const base = delMatch[2] // 可能为 undefined 或空字符串
    if (base && base.length > 0) {
      return `${formatPosition(posStr)}，缺失碱基"${base}"`
    }
    return `${formatPosition(posStr)}，发生缺失突变`
  }

  // C. 插入 (ins): c.123insA 或 c.123_124insTC
  // 捕获组1: 位置, 捕获组2: 插入的碱基序列
  const insRegex = /(?:C\.)?(\d+(?:_\d+)?)INS([ATGC]+)$/
  const insMatch = cleanStr.match(insRegex)
  if (insMatch && insMatch[1]) {
    return `${formatPosition(insMatch[1])}，插入碱基序列"${insMatch[2]}"`
  }

  // D. 替换 (sub): c.123A>T
  // 捕获组1: 位置, 捕获组2: 原碱基, 捕获组3: 新碱基
  const subRegex = /(?:C\.)?(\d+)([ATGC])>([ATGC])$/
  const subMatch = cleanStr.match(subRegex)
  if (subMatch && subMatch[1]) {
    return `第${subMatch[1]}位碱基，由"${subMatch[2]}"变为"${subMatch[3]}"`
  }

  // E. 缺失插入 (delins): c.123_124delinsAG
  const delinsRegex = /(?:C\.)?(\d+(?:_\d+)?)DELINS([ATGC]+)$/
  const delinsMatch = cleanStr.match(delinsRegex)
  if (delinsMatch && delinsMatch[1]) {
    return `${formatPosition(delinsMatch[1])}，缺失并插入碱基序列"${
      delinsMatch[2]
    }"`
  }

  return '无法识别的碱基变异格式'
}

/**

 * 解析各种类型的氨基酸变异

 * 支持格式：

 * 1. 替换突变: p.Arg337Ter

 * 2. 移码突变: p.Leu79GlufsTer84

 * 3. 缺失突变: p.Gly123del

 * 4. 插入突变: p.Ala150_Pro151insSer

 * 

 * @param {string} variant - HGVS格式的变异字符串

 * @returns {string} 自然语言描述的变异信息

 */
export function formatAminoAcidMutation(variant: string) {
  // 统一处理 NP_000401.1:p.His63Asp 和 p.His63Asp 两种格式

  const pMatch = variant.match(/:?p\.(.+)/)
  const cleanStr = pMatch ? pMatch[1] : variant

  // 匹配各种变异类型的正则表达式

  const patterns = [
    // 移码突变: Val653AlafsTer50 (新增，放在第一位)
    {
      regex: /^([A-Z][a-z]{2})(\d+)([A-Z][a-z]{0,3})fsTer(\d+)$/i,
      handler: (match: [any, any, any, any, any]) => {
        const [, originalCode, position, mutatedStart, terPosition] = match
        return `第${position}位氨基酸由${lookupAminoAcid(
          originalCode
        )}(${originalCode})变为${
          mutatedStart ? lookupAminoAcid(mutatedStart) : '未知氨基酸'
        }(${mutatedStart})，并在突变下游第${terPosition}个氨基酸位置产生终止密码子`
      }
    },

    // 移码突变: Leu79GlufsTer84
    {
      regex: /^([A-Z][a-z]{2})(\d+)([A-Z][a-z]{0,3})fsTer(\d+)$/,

      handler: (match: [any, any, any, any, any]) => {
        const [, originalCode, position, mutatedStart, terPosition] = match
        return `第${position}位氨基酸由${lookupAminoAcid(
          originalCode
        )}(${originalCode})变为${
          mutatedStart ? lookupAminoAcid(mutatedStart) : '未知氨基酸'
        }(${mutatedStart})，并在突变下游第${terPosition}个氨基酸位置产生终止密码子`

        // return (
        //   `第${position}位氨基酸由${lookupAminoAcid(
        //     originalCode
        //   )}(${originalCode})发生移码突变，` +
        //   `导致${
        //     mutatedStart ? lookupAminoAcid(mutatedStart) : '未知氨基酸'
        //   }(${mutatedStart})开始出现移码，` +
        //   `并在第${terPosition}号位置提前终止，形成终止密码子(Ter)`
        // )
      }
    },

    // 替换突变: Arg337Ter

    {
      regex: /^([A-Z][a-z]{2})(\d+)([A-Z][a-z]{0,3})?$/,

      handler: (match: [any, any, any, any]) => {
        const [, originalCode, position, mutatedCode] = match

        const mutatedType = mutatedCode || 'Ter'

        return (
          `第${position}位氨基酸由${lookupAminoAcid(
            originalCode
          )}(${originalCode})变为` +
          `${lookupAminoAcid(mutatedType)}(${mutatedType})`
        )
      }
    },

    // 缺失突变: Gly123del

    {
      regex: /^([A-Z][a-z]{2})(\d+)(del.*)?$/,

      handler: (match: [any, any, any]) => {
        const [, originalCode, position] = match

        return `第${position}位氨基酸${lookupAminoAcid(
          originalCode
        )}(${originalCode})发生缺失突变`
      }
    },

    // 插入突变: Ala150_Pro151insSer

    {
      regex: /^([A-Z][a-z]{2})(\d+)_([A-Z][a-z]{2})(\d+)ins([A-Z][a-z]{0,3})$/,

      handler: (match: [any, any, any, any, any, any]) => {
        const [, startCode, startPos, endCode, endPos, insertedCode] = match

        return (
          `在第${startPos}位(${lookupAminoAcid(startCode)}(${startCode})和` +
          `第${endPos}位(${lookupAminoAcid(endCode)}(${endCode}))之间` +
          `插入${lookupAminoAcid(insertedCode)}(${insertedCode})`
        )
      }
    }
  ]

  for (const pattern of patterns) {
    const match = cleanStr.match(pattern.regex)

    if (match) return pattern.handler(match as any)
  }
  console.error(`无法解析的变异格式: ${variant}`)
  return ''

  // throw new Error(`无法解析的变异格式: ${variant}`)
}

/**

 * 氨基酸代码转换及验证

 * @param {string} code - 氨基酸三字母代码

 * @returns {string} 氨基酸全称

 */

function lookupAminoAcid(code: string | number) {
  const fullName = AMINO_ACID_MAP[code]

  if (!fullName) throw new Error(`无效的氨基酸代码: ${code}`)

  return fullName
}

export function formatSpecialInheritance(Inheritance: string) {
  // 创建acronym到zh的映射Map

  const map = new Map(specialInheritance.map((item) => [item.acronym, item.zh]))

  // 分割输入字符串为数组

  const acronyms = Inheritance.split(',').map((item) => item.trim())

  // 替换每个缩写

  const replaced = acronyms.map((acronym) => {
    return map.has(acronym) ? map.get(acronym) : acronym
  })

  // 重新连接为字符串

  return replaced.join(',')
}

export function renameChildTypes(familyArray: any[]) {
  const children = familyArray
    .filter((item) => item)
    .filter((member) => member.type.includes('child'))

  const childCount = children.length

  if (childCount === 0) return familyArray
  const arr = familyArray.map((member) => {
    if (!member.type.includes('child')) {
      return member
    } else if (member.type.includes('child')) {
      if (childCount === 1) {
        return member
      } else {
        // 找到当前孩子在孩子数组中的索引

        const index =
          children.findIndex((c) =>
            c.id ? c.id === member.id : c.sample_id === member.sample_id
          ) + 1

        return {
          ...member,

          type: `${member.type}//${index}`
        }
      }
    }

    return member
  })

  return arr
}

export function calculateAndFormat(inputStr: string, multiplier = 1) {
  try {
    // 清理输入字符串（移除千分位逗号）

    const cleanStr = inputStr.replace(/,/g, '')

    // 转换为数字类型

    const number = parseFloat(cleanStr)

    // 验证数字有效性

    if (isNaN(number)) {
      throw new Error('Invalid input: Non-numeric characters detected')
    }

    // 执行计算

    const result = number * multiplier

    // 格式化结果

    return formatNumberWithCommas(result)
  } catch (error) {
    console.error('Processing failed:', error)

    return 'Error: Invalid input format'
  }
}

export function formatNumberWithCommas(num: number) {
  // 处理小数部分

  try {
    const parts = num.toString().split('.')

    // 格式化整数部分（支持负数）

    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',')

    return parts.join('.')
  } catch (error) {
    return `${num}`
  }
}

export function switchAnalysisType(type: string) {
  switch (type) {
    case 'family_x_linked':
      return 'trio_is_x_linked'
    case 'family_acmg':
      return 'trio_is_acmg_sf'
    case 'family_fast_track':
      return 'is_fast_track'
    case 'family_dominant':
      return 'trio_is_dominant'
    case 'family_homozygous':
      return 'trio_is_homozygous'
    case 'family_compound_heterozygous':
      return 'trio_is_compound_het'
    case 'family_denovo':
      return 'trio_is_denovo'
    case 'carrier_fast_track':
      return 'carrier_is_fast_track'
    case 'carrier_hom_hemi':
      return 'carrier_is_hom_hemi'
    case 'carrier_compound_heterozygous':
      return 'carrier_is_compound_het'
    case 'carrier_acmg':
      return 'carrier_is_acmg_sf'
    case 'carrier_mt':
      return 'carrier_is_mt'
    default:
      return type
  }
}

export function switchACMG(acmg: string) {
  switch (acmg) {
    case 'Pathogenic':
      return 'PAT'
    case 'pathogenic':
      return 'PAT'
    case 'PAT':
      return 'pathogenic'
    case 'Likely pathogenic':
      return 'LP'
    case 'likely pathogenic':
      return 'LP'
    case 'LP':
      return 'likely pathogenic'
    case 'Benign':
      return 'BEN'
    case 'benign':
      return 'BEN'
    case 'BEN':
      return 'benign'
    case 'Uncertain significance':
      return 'VUS'
    case 'uncertain significance':
      return 'VUS'
    case 'VUS':
      return 'Uncertain significance'
    case 'Likely benign':
      return 'LB'
    case 'likely benign':
      return 'LB'
    case 'LB':
      return 'Likely benign'
    case 'drug response':
      return 'DRUG'
    case 'DRUG':
      return 'drug response'
    default:
      return acmg
  }
}

export function switchInheritance(inheritance: string) {
  switch (inheritance) {
    case 'autosomal recessive':
      return 'AR'
    case 'autosomal dominant':
      return 'AD'
    case 'somatic':
      return 'X-D'
    case 'x-linked':
      return 'X-R'
    default:
      return inheritance
  }
}

interface PickerSelection {
  headers: string[] // display names of checked group headers, e.g. ["Exon"]
  children: string[] // checked child tokens, e.g. ["missense", "splice_acceptor"]
}

export function tokensFromSelection(sel: PickerSelection): string[] {
  const byHeader = new Map(PICKER_GROUPS.map((g) => [g.header, g]))
  const groups = sel.headers
    .map((h) => byHeader.get(h))
    .filter((g): g is PickerGroup => !!g)
  const headerTokens = groups.map((g) => g.headerToken)
  const covered = new Set(groups.flatMap((g) => g.children.map((c) => c.token)))
  const childTokens = sel.children.filter((t) => !covered.has(t))
  return [...headerTokens, ...childTokens]
}

export function buildOntologyFilters(sequenceOntology: string[]): Clause[] {
  if (!sequenceOntology?.length) return []

  const labels = new Set<string>()
  const locations = new Set<string>()
  const extraClauses: Clause[] = []

  for (const t of sequenceOntology) {
    if (COMPOUND_TOKENS[t]) {
      extraClauses.push(...COMPOUND_TOKENS[t])
      continue
    }
    ;(TOKEN_TO_LABELS[t] ?? []).forEach((v) => labels.add(v))
    ;(TOKEN_TO_LOCATIONS[t] ?? []).forEach((v) => locations.add(v))

    // Tokens that match nothing (incl. UNSUPPORTED_TOKENS) contribute no clause.
  }

  const simpleClauses: Clause[] = []
  if (labels.size)
    simpleClauses.push({
      field: 'consequence_labels',
      op: 'contains',
      value: [...labels]
    })
  if (locations.size)
    simpleClauses.push({
      field: 'variant_locations',
      op: 'contains',
      value: [...locations]
    })

  const all = [...simpleClauses, ...extraClauses]
  if (all.length <= 1) return all
  return [{ or: all }] // flat OrGroup — legacy `$or` semantics preserved
}

export function changeV1Filter(obj: NoObject) {
  const tokenToLabels = obj.tokenToLabels
  const tokenToLocations = obj.tokenToLocations
  const sequenceOntology: string[] = []
  if (tokenToLabels) {
    sequenceOntology.push(...tokenToLabels)
  }
  if (tokenToLocations) {
    sequenceOntology.push(...tokenToLocations)
  }
  const filters: any[] = []
  const or: any[] = []
  if (sequenceOntology && sequenceOntology.length) {
    const data: PickerSelection = {
      headers: [],
      children: sequenceOntology
    }

    PICKER_GROUPS.map((items) => {
      const newChild = items.children.filter((item) =>
        sequenceOntology.includes(item.label)
      )
      if (newChild && newChild.length) {
        data.headers.push(items.header)
      }
    })
    filters.push(...buildOntologyFilters(tokensFromSelection(data)))
  }
  Object.getOwnPropertyNames(obj).forEach((key) => {
    const value = obj[key]
    if (key === 'chrom') {
      filters.push({
        field: 'chrom',
        op: 'in',
        value: value
      })
    } else if (key === 'ad') {
      value.map((item: any) => {
        const zygosity = item.zygosity
        const alleleFrequencyOperator = item.alleleFrequencyOperator
        const alleleFrequencyValue = item.alleleFrequencyValue
        const qualMax = item.qualMax
        const qualMin = item.qualMin
        const readDepthOperator = item.readDepthOperator
        const readDepthValue = item.readDepthValue

        if (zygosity === 'homozygote')
          filters.push({
            field: 'allele_balance',
            op: 'gte',
            value: 0.85
          })
        else if (zygosity === 'heterozygote')
          filters.push({
            field: 'allele_balance',
            op: 'lt',
            value: 0.85
          })
        if (qualMax)
          filters.push({
            field: 'qual',
            op: 'lte',
            value: qualMax
          })
        if (qualMin)
          filters.push({
            field: 'qual',
            op: 'gte',
            value: qualMin
          })
        if (alleleFrequencyValue) {
          filters.push({
            field: 'allele_balance',
            op: alleleFrequencyOperator,
            value: alleleFrequencyValue
          })
        }
        if (readDepthValue) {
          filters.push({
            field: 'dp',
            op: readDepthOperator,
            value: readDepthValue
          })
        }
      })
    } else if (key === 'inheritance') {
      filters.push({
        field: 'inheritances',
        op: 'contains',
        value: value.map((item: string) => switchInheritance(item))
      })
    } else if (key === 'CopyNumber') {
      filters.push({
        field: 'cn',
        op: 'eq',
        value: `${value}`
      })
    } else if (key === 'posStart') {
      filters.push({
        field: 'pos',
        op: 'gte',
        value: value
      })
    } else if (key === 'zygosity') {
      if (value === 'homozygote')
        filters.push({
          field: 'allele_balance',
          op: 'gte',
          value: 0.85
        })
      else if (value === 'heterozygote')
        filters.push({
          field: 'allele_balance',
          op: 'lt',
          value: 0.85
        })
    } else if (key === 'is_ROH') {
      filters.push({
        field: 'is_roh',
        op: 'eq',
        value: Boolean(value)
      })
    } else if (key === 'is_loss_of_function') {
      filters.push({
        field: 'is_loss_of_function',
        op: 'eq',
        value: Boolean(value)
      })
    } else if (key === 'hasPubmed') {
      filters.push({
        field: 'pubmed_ids',
        op: value ? 'is_not_empty' : 'is_empty',
        value: null
      })
    } else if (key === 'posEnd') {
      filters.push({
        field: 'pos',
        op: 'lte',
        value: value
      })
    } else if (key === 'geneNameIn') {
      if (
        (obj.glsphenotypes && obj.glsphenotypes.length) ||
        (obj.glsphenotypesCNV && obj.glsphenotypesCNV.length)
      ) {
        //
      } else
        filters.push({
          field: 'gene',
          op: 'in',
          value: value
        })
    } else if (key === 'geneNameOut') {
      filters.push({
        field: 'gene',
        op: 'not_in',
        value: value
      })
    } else if (key === 'qualMin') {
      filters.push({
        field: 'qual',
        op: 'gte',
        value: value
      })
    } else if (key === 'qualMax') {
      filters.push({
        field: 'qual',
        op: 'lte',
        value: value
      })
    } else if (key === 'ownerIndexMin') {
      filters.push({
        field: 'user_index',
        op: 'gte',
        value: value
      })
    } else if (key === 'ownerIndexMax') {
      filters.push({
        field: 'user_index',
        op: 'lte',
        value: value
      })
    } else if (key === 'indexMin') {
      filters.push({
        field: 'platform_index',
        op: 'gte',
        value: value
      })
    } else if (key === 'indexMax') {
      filters.push({
        field: 'platform_index',
        op: 'lte',
        value: value
      })
    } else if (key === 'organizationIndexMin') {
      filters.push({
        field: 'organization_index',
        op: 'gte',
        value: value
      })
    } else if (key === 'organizationIndexMax') {
      filters.push({
        field: 'organization_index',
        op: 'lte',
        value: value
      })
    } else if (key === 'CLINVAR_clnsig_final') {
      // value?.map((item: string) => {
      //   filters.push({
      //     field: 'clinvar_clinsig_final',
      //     op: 'contains',
      //     value: item
      //   })
      // })
      if (value.some((i: string) => i === '.')) {
        if (value.filter((i: string) => i !== '.').length > 0) {
          or.push({ field: 'clinvar_clinsig_final', op: 'is_null' })
          or.push({
            field: 'clinvar_clinsig_final',
            op: 'contains',
            value: value?.map((item: string) => item)
          })
        } else {
          filters.push({
            field: 'clinvar_clinsig_final',
            op: 'is_null'
          })
        }
      } else
        filters.push({
          field: 'clinvar_clinsig_final',
          op: 'contains',
          value: value?.map((item: string) => item)
        })
    } else if (key === 'acmgClassification' && value.length > 0) {
      filters.push({
        field: 'acmg_classification',
        op: 'contains',
        value: value?.map((item: string) => switchACMG(item))
      })
    } else if (key === 'classification') {
      if (value.some((i: string) => i === 'loss of function')) {
        if (value.filter((i: string) => i !== 'loss of function').length > 0) {
          or.push({
            field: 'gls_classification',
            op: 'contains',
            value: value
              .filter((i: string) => i !== 'loss of function')
              ?.map((item: string) => switchACMG(item))
          })
          or.push({
            field: 'is_loss_of_function',
            op: 'eq',
            value: Boolean(value)
          })
        } else {
          filters.push({
            field: 'is_loss_of_function',
            op: 'eq',
            value: Boolean(value)
          })
        }
      } else {
        filters.push({
          field: 'gls_classification',
          op: 'contains',
          value: value?.map((item: string) => switchACMG(item))
        })
      }
    } else if (key === 'ccnv_classification') {
      or.push({
        field: 'ccnv_classification',
        op: 'contains',
        value: value?.map((item: string) => switchACMG(item))
      })
      or.push({
        field: 'classification',
        op: 'contains',
        value: value?.map((item: string) => switchACMG(item))
      })
    } else if (key === 'sv_classification') {
      filters.push({
        field: 'classification',
        op: 'contains',
        value: value?.map((item: string) => switchACMG(item))
      })
    } else if (key === 'indelValue' && value) {
      if (obj.snvValue) {
        or.push({
          and: [
            { field: 'variant_type', op: 'eq', value: 'INDEL' },
            { field: 'allele_balance', op: obj.indelOperator, value: value }
          ]
        })
        or.push({
          and: [
            { field: 'variant_type', op: 'eq', value: 'SNV' },
            {
              field: 'allele_balance',
              op: obj.snvOperator,
              value: obj.snvValue
            }
          ]
        })
      } else {
        filters.push({ field: 'variant_type', op: 'eq', value: 'INDEL' })
        filters.push({
          field: 'allele_balance',
          op: obj.indelOperator,
          value: Number(value)
        })
      }
    } else if (key === 'snvValue' && value) {
      if (obj.indelValue) {
        //
      } else {
        filters.push({ field: 'variant_type', op: 'eq', value: 'SNV' })
        filters.push({
          field: 'allele_balance',
          op: obj.snvOperator,
          value: Number(value)
        })
      }
    }

    // else if (key === 'tokenToLabels' && value) {
    //   const tokenToLocations =
    //     obj.tokenToLocations && obj.tokenToLocations.length >= 2
    //       ? obj.tokenToLocations
    //       : obj.tokenToLocations
    //       ? obj.tokenToLocations.join('')
    //       : ''
    //   const tokenToLabels =
    //     obj.tokenToLabels && obj.tokenToLabels.length >= 2
    //       ? obj.tokenToLabels
    //       : obj.tokenToLabels.join('')
    //   if (tokenToLocations) {
    //     or.push({
    //       field: 'consequence_labels',
    //       op: 'contains',
    //       value: tokenToLabels
    //     })
    //     or.push({
    //       field: 'variant_locations',
    //       op: 'contains',
    //       value: tokenToLocations
    //     })
    //   } else {
    //     filters.push({
    //       field: 'consequence_labels',
    //       op: 'contains',
    //       value: tokenToLabels
    //     })
    //   }
    // } else if (key === 'tokenToLocations' && value) {
    //   const tokenToLocations =
    //     obj.tokenToLocations && obj.tokenToLocations.length >= 2
    //       ? obj.tokenToLocations
    //       : obj.tokenToLocations.length.join('')
    //   const tokenToLabels =
    //     obj.tokenToLabels && obj.tokenToLabels.length >= 2
    //       ? obj.tokenToLabels
    //       : obj.tokenToLabels
    //       ? obj.tokenToLabels.join('')
    //       : ''
    //   if (tokenToLabels) {
    //     //
    //   } else {
    //     filters.push({
    //       field: 'variant_locations',
    //       op: 'contains',
    //       value: tokenToLocations
    //     })
    //   }
    // }
    else if (key === 'acmgScoreValue' && (value || value === 0)) {
      filters.push({
        field: 'acmg_score',
        op: obj.acmgScoreOperator,
        value: Number(value)
      })
    } else if (key === 'readDepthValue' && (value || value === 0)) {
      filters.push({
        field: 'dp',
        op: obj.readDepthOperator,
        value: Number(value)
      })
    } else if (key === 'fscoreValue' && value) {
      or.push({
        field: 'f_score',
        op: obj.fscoreOperator,
        value: Number(value) / 100
      })
    } else if (key === 'gscoreValue' && value) {
      or.push({
        field: 'g_score',
        op: obj.gscoreOperator,
        value: Number(value)
      })
    } else if (key === 'fmaxscoreValue' && value) {
      or.push({
        field: 'f_score_max',
        op: obj.fscoreOperator,
        value: Number(value) / 100
      })
    } else if (key === 'gmaxscoreValue' && value) {
      or.push({
        field: 'g_score_max',
        op: obj.gscoreOperator,
        value: Number(value)
      })
    } else if (key === 'alphaMValue' && value) {
      or.push({
        field: 'am_pathogenicity',
        op: obj.alphaMOperator,
        value: `${value}`
      })
    } else if (key === 'spliceAIValue' && value) {
      or.push({
        field: 'spliceai_max_score',
        op: obj.spliceAIOperator,
        value: Number(value)
      })
    } else if (key === 'revelValue' && value) {
      or.push({
        field: 'revel',
        op: obj.revelOperator,
        value: String(value)
      })
    } else if (key === 'rsId' && value) {
      filters.push({
        field: 'rsid',
        op: 'regex',
        value: value
      })
    } else if (key === 'glsphenotypes' && value) {
      if (obj.geneNameIn && obj.geneNameIn.length) {
        or.push({
          field: 'gene',
          op: 'in',
          value: obj.geneNameIn
        })
        or.push({
          field: 'g_score_hpo_codes',
          op: 'contains',
          value: value.map((i: any) => i.hpo_code)
        })
      }
    } else if (key === 'glsphenotypesCNV' && value) {
      if (obj.geneNameIn && obj.geneNameIn.length) {
        or.push({
          field: 'gene',
          op: 'in',
          value: obj.geneNameIn
        })
      }
    } else if (key === 'RU' && value) {
      filters.push({
        field: 'repeat_unit',
        op: 'contains',
        value: value
      })
    } else if (key === 'expansionHunterRefValue' && value) {
      filters.push({
        field: 'eh_ref',
        op: obj.expansionHunterRefOperator,
        value: String(value)
      })
    } else if (key === 'gangStrDpValue' && value) {
      filters.push({
        field: 'gangstr_dp',
        op: obj.gangStrDpOperator,
        value: String(value)
      })
    } else if (key === 'gangStrQualityValue' && value) {
      filters.push({
        field: 'gangstr_q',
        op: obj.gangStrQualityOperator,
        value: String(value)
      })
    } else if (key === 'hasPositiveHighlight') {
      filters.push({
        field: 'scip_has_positive_highlight',
        op: 'eq',
        value: value ? 1 : 0
      })
    } else if (key === 'hasNegativeHighlight') {
      filters.push({
        field: 'scip_has_negative_highlight',
        op: 'eq',
        value: value ? 1 : 0
      })
    } else if (key === 'scipOverlapRateValue' && value) {
      filters.push({
        field: 'scip_overlap_rates',
        op: obj.scipOverlapRateOperator,
        value: value
      })
    } else if (key === 'scipGnomAdOverlapRateValue' && value) {
      filters.push({
        field: 'scip_gnomad_overlap_rates',
        op: obj.scipGnomAdOverlapRateOperator,
        value: value
      })
    } else if (key === 'scipClinvarOverlapRateValue' && value) {
      filters.push({
        field: 'scip_clinvar_overlap_rates',
        op: obj.scipClinvarOverlapRateOperator,
        value: value
      })
    } else if (key === 'scipGeneOverlapRateValue' && value) {
      filters.push({
        field: 'scip_gene_overlap_rates',
        op: obj.scipGeneOverlapRateOperator,
        value: value
      })
    } else if (key === 'scipGenePliValue' && value) {
      filters.push({
        field: 'scip_gene_pli_list',
        op: obj.scipGenePliOperator,
        value: value
      })
    } else if (key === 'scipGeneLoeufValue' && value) {
      filters.push({
        field: 'scip_gene_loeuf_list',
        op: obj.scipGeneLoeufOperator,
        value: value
      })
    } else if (key === 'scipGeneHiIn') {
      filters.push({
        field: 'scip_clingen_sv',
        op: 'contains',
        value: value
      })
    } else if (key === 'scipGeneTsIn') {
      filters.push({
        field: 'scip_clinical_genome_data',
        op: 'contains',
        value: value
      })
    } else if (key === 'scipClingenTriploIn') {
      filters.push({
        field: 'scipClingenTriploIn',
        op: 'contains',
        value: value
      })
    } else if (key === 'scipClingenHaploIn') {
      filters.push({
        field: 'scipClingenHaploIn',
        op: 'contains',
        value: value
      })
    } else if (key === 'lengthValue' && value) {
      or.push({
        field: 'sv_length',
        op: obj.lengthOperator,
        value: Number(value)
      })
    } else if ((key === 'SVType' || key === 'sv_type') && value) {
      or.push({
        field: 'sv_type',
        op: 'contains',
        value: value
      })
    } else if (
      key === 'gnomAD' &&
      value &&
      Array.isArray(value) &&
      value.length &&
      obj.gnomADValue
    ) {
      value.forEach((item: string) => {
        filters.push({
          field: item,
          op: obj.gnomADOperator,
          value: String(obj.gnomADValue)
        })
      })
    }
  })
  if (or.length) {
    filters.push({
      or: or
    })
  }

  return filters
}

export function formatScientificToDecimal(value: any) {
  if (value === '.') return value
  const num = Number(value)
  if (num === 0) return '0'

  // 判断是否为科学计数法格式（如 "2.522e-05"）
  const scientificRegex = /^[+-]?\d+\.?\d*[eE][+-]?\d+$/
  if (typeof value === 'string' && scientificRegex.test(value)) {
    const num = parseFloat(value)
    if (isNaN(num)) return value

    // 计算需要的小数位数
    const exponent = Math.floor(Math.log10(Math.abs(num)))
    const decimalPlaces = Math.max(0, -exponent + 1)
    return num.toFixed(decimalPlaces)
  }

  // 计算需要的小数位数
  const exponent = Math.floor(Math.log10(Math.abs(num)))
  const decimalPlaces = Math.max(0, -exponent + 1) // +1 保留整数部分

  // 使用 toFixed 格式化
  return num.toFixed(decimalPlaces)
}

/**
 * 去除 HLA 等位基因字符串中的基因前缀
 * 例如: "HLA-A*11:01:01" -> "11:01:01"
 * @param {string} alleleStr - 完整的等位基因字符串
 * @returns {string} - 去除基因名后的字符串
 */
export function cleanAllele(alleleStr: string) {
  if (!alleleStr || typeof alleleStr !== 'string') return alleleStr

  // 方法：查找星号 '*' 的位置，截取之后的部分
  const starIndex = alleleStr.indexOf('*')
  if (starIndex !== -1) {
    return alleleStr.substring(starIndex + 1)
  }

  // 如果没有星号，可能格式不同，返回原字符串或根据需求处理
  return alleleStr
}

export function changeV1Sorter(obj: NoObject) {
  const sorter: any[] = []
  console.log('obj', obj)
  Object.getOwnPropertyNames(obj).forEach((key) => {
    const value = obj[key]
    if (key === 'chrom') {
      sorter.push({
        field: 'chrom',
        order: value === 1 ? 'asc' : 'desc'
      })
    } else if (key === 'gene') {
      sorter.push({
        field: 'gene',
        order: value === 1 ? 'asc' : 'desc'
      })
    } else if (key === 'inputPos') {
      // sorter.push({
      //   field: 'pos',
      //   order: value === 1 ? 'asc' : 'desc',
      // })
    } else if (key === 'f_score') {
      sorter.push({
        field: 'f_score',
        order: value === 1 ? 'asc' : 'desc'
      })
    } else if (key === 'g_score') {
      sorter.push({
        field: 'g_score',
        order: value === 1 ? 'asc' : 'desc'
      })
    } else if (key === 'f_score_max') {
      sorter.push({
        field: 'f_score_max',
        order: value === 1 ? 'asc' : 'desc'
      })
    } else if (key === 'g_score_max') {
      sorter.push({
        field: 'g_score_max',
        order: value === 1 ? 'asc' : 'desc'
      })
    } else if (key === 'ACMG_priority') {
      sorter.push({
        field: 'acmg_pathogenicity_priority',
        order: value === 1 ? 'asc' : 'desc'
      })
    } else if (key === 'ACMG_score') {
      sorter.push({
        field: 'acmg_score',
        order: value === 1 ? 'asc' : 'desc'
      })
    } else if (key === 'fast_track_priority') {
      sorter.push({
        field: 'fast_track_priority',
        order: value === 1 ? 'asc' : 'desc'
      })
    } else if (key === 'scip_summary_priority') {
      sorter.push({
        field: 'scip_summary_priority',
        order: value === 1 ? 'asc' : 'desc'
      })
    } else if (key === 'ccnv_priority') {
      sorter.push({
        field: 'ccnv_priority',
        order: value === 1 ? 'asc' : 'desc'
      })
    } else if (key === 'cn') {
      sorter.push({
        field: 'cn',
        order: value === 1 ? 'asc' : 'desc'
      })
    }
  })

  return sorter
}

const MAX_COMMITS = 50

/**
 * 从文献描述字符串中提取 PMID 数组
 * @param {string} text - 包含 PMID 信息的原始字符串
 * @returns {string[]} - 提取到的纯数字 PMID 数组
 */
export function extractPmidsFromText(text: string) {
  if (!text || typeof text !== 'string') {
    return []
  }

  // 步骤 1: 定位 PMID 列表的开始位置
  // 查找 "PMID:" 不区分大小写
  const startIndex = text.search(/PMID:/i)
  if (startIndex === -1) {
    return []
  }

  // 步骤 2: 截取 PMID: 之后的字符串
  const substring = text.slice(startIndex + 5) // "PMID:".length is 5

  // 步骤 3: 使用正则提取所有连续的数字序列
  // \d+ 匹配一个或多个数字
  // g 标志全局匹配
  const matches = substring.match(/\d+/g)

  if (!matches) {
    return []
  }

  // 步骤 4: 过滤和清理
  // 通常 PMID 是 7 到 9 位数字。
  // 注意：如果字符串中有像 "3619146635482138" 这样连在一起的长数字，
  // 它会被当作一个整体匹配出来。如果这是两个 PMID 连写（例如 36191466 和 35482138），
  // 简单的 \d+ 无法自动拆分。但在没有明确分隔符的情况下，我们只能保留原始匹配。
  // 这里我们假设提取出的数字串即为候选 PMID。

  // 可选：过滤掉明显不是 PMID 的长度（例如小于 5 位或大于 10 位，视具体数据质量而定）
  // 此处为了通用性，返回所有匹配到的数字串，并去重
  const uniquePmids = [...new Set(matches)]

  return uniquePmids
}

export function changeAcmgText(acmgs: string[]) {
  if (!acmgs || !acmgs.length) {
    return ''
  }

  const acmgText = []
  if (acmgs.some((item) => item === '致病')) {
    acmgText.push('致病')
  }
  if (acmgs.some((item) => item === '可能致病')) {
    acmgText.push('可能致病')
  }
  if (acmgs.some((item) => item === '意义未明')) {
    acmgText.push('意义未明')
  }
  if (acmgs.some((item) => item === '可能良性')) {
    acmgText.push('可能良性')
  }
  if (acmgs.some((item) => item === '良性')) {
    acmgText.push('良性')
  }
  return acmgText.join('/')
}
interface InputItem {
  HPO_id: string
  gene_names: string[]
}

interface OutputItem {
  gene: string
  hpo_count: number
  hpo: string[]
  id: number
}

/**
 * 处理对象数组，按基因聚合 HPO ID 并排序
 * @param data 输入的对象数组
 * @returns 处理后的数组，按 hpo_count 降序排列
 */
export function processGeneHpoData(data: InputItem[]): OutputItem[] {
  // 1. 使用 Map 进行数据聚合
  // Key: gene name, Value: Set of HPO_ids (使用 Set 自动去重 HPO_id)
  const geneMap = new Map<string, Set<string>>()

  data.forEach((item) => {
    const { HPO_id, gene_names } = item

    // 遍历当前项中的所有基因
    gene_names.forEach((gene) => {
      if (!geneMap.has(gene)) {
        geneMap.set(gene, new Set())
      }

      // 将 HPO_id 添加到该基因对应的集合中
      geneMap.get(gene)?.add(HPO_id)
    })
  })

  // 2. 将 Map 转换为数组，并构造最终对象结构
  let result: OutputItem[] = Array.from(geneMap.entries()).map(
    ([gene, hpoSet], index) => {
      const hpoArray = Array.from(hpoSet)
      return {
        gene: gene,
        hpo: hpoArray,
        hpo_count: hpoArray.length,
        id: index // 注意：这里的 id 是转换过程中的临时索引，通常建议在排序后重新分配或根据需求定义
      }
    }
  )

  // 3. 按照 hpo_count 从高到低排序
  result.sort((a, b) => b.hpo_count - a.hpo_count)

  // 4. (可选) 如果要求 id 必须反映最终排序后的位置（即排名），可以重新映射 id
  // 如果 id 仅指代原始处理顺序，可忽略此步。通常“index”在结果集中指代当前行的序号。
  result = result.map((item, index) => ({
    ...item,
    id: index
  }))

  return result
}

// 获取最近提交记录

// export async function getRecentCommits(): Promise<any[]> {
//   try {
//     const command = `git log -n ${MAX_COMMITS} --pretty=format:"%h|%an|%ad|%s"`

//     const output = execSync(command, {
//       encoding: 'utf-8',
//       cwd: process.cwd()
//     })

//     return parseGitLog(output)
//   } catch (error) {
//     console.error('Git command error:', error)

//     return []
//   }
// }

// // 解析 Git 日志

// function parseGitLog(log: string): any[] {
//   return log
//     .split('\n')

//     .filter((line) => line.trim())

//     .map((line) => {
//       const [hash, author, date, message] = line.split('|')

//       return {
//         hash,

//         author,

//         date: new Date(date),

//         message: message.replace(/\\n/g, ' ') // 处理换行符
//       }
//     })
// }
